import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { AuthContext } from './AuthContext';
import {urlApi} from  '../configuracion/serverConfig';
import { toast } from 'react-toastify';

const POLL_INTERVAL_MS = 10000;

// Devuelve { valida: true }, { valida: false, mensaje } cuando el backend rechaza la sesión, o null
// cuando no se pudo saber: backend caído o reiniciándose, error HTTP o de base de datos. Un null no
// debe cerrar la sesión; antes cada reinicio del backend la cerraba a todos los usuarios conectados.
const verificarSesion = (sesionId, usuario) =>
    fetch(`${urlApi}/usuario/login/${sesionId}/${usuario}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            Accept: 'application/json'
        }
    })
    .then(res => res.ok ? res.json() : null)
    .then(data => {
        if (!data) return null;
        if (data.status === 0) return { valida: true };
        // El procedimiento responde "La sesión es invalida"; los demás errores no dicen nada de la
        // sesión. Es el mismo criterio del interceptor de fetch de más abajo.
        const mensaje = data.objError?.msgError || '';
        return mensaje.toLowerCase().includes('sesi') ? { valida: false, mensaje } : null;
    })
    .catch(() => null);

const AuthProvider = (props) => {

    const [auth, setAuth] = useState(undefined);
    const [sinConexionInicial, setSinConexionInicial] = useState(false);
    const [sesionExpirada, setSesionExpirada] = useState({ visible: false, mensaje: '' });
    const intervalRef = useRef(null);
    const reintentoRef = useRef(null);
    const yaDetectadaRef = useRef(false);
    const sinConexionRef = useRef(false);

    const invalidarSesion = () => {
        sessionStorage.removeItem('Usuario');
        setAuth(false);
    };

    const mostrarAlertaSesion = (mensaje) => {
        if (yaDetectadaRef.current) return;
        yaDetectadaRef.current = true;
        clearInterval(intervalRef.current);
        setSesionExpirada({ visible: true, mensaje: mensaje || 'La sesión ha expirado.' });
    };

    const mostrarAlertaSesionRef = useRef(mostrarAlertaSesion);

    const confirmarSesionExpirada = () => {
        yaDetectadaRef.current = false;
        setSesionExpirada({ visible: false, mensaje: '' });
        invalidarSesion();
    };

    useEffect(() => {
        const objSesion = JSON.parse(sessionStorage.getItem('Usuario'));
        if (objSesion !== null && objSesion.hasOwnProperty("sesionId") && objSesion.hasOwnProperty("usuario")) {
            // Sin conexión no se entra (el tablero falla si no carga sus datos) ni se cierra la
            // sesión: se reintenta hasta que el backend responda.
            const verificarAlCargar = () => verificarSesion(objSesion.sesionId, objSesion.usuario).then(resultado => {
                if (!resultado) {
                    setSinConexionInicial(true);
                    reintentoRef.current = setTimeout(verificarAlCargar, 5000);
                    return;
                }
                setSinConexionInicial(false);
                if (resultado.valida) {
                    setAuth(true);
                } else {
                    invalidarSesion();
                    if (resultado.mensaje) toast.info(resultado.mensaje);
                }
            });
            verificarAlCargar();
        } else {
            setAuth(false);
        }
        return () => clearTimeout(reintentoRef.current);
    }, []);

    useEffect(() => {
        mostrarAlertaSesionRef.current = mostrarAlertaSesion;
    });

    useEffect(() => {
        if (auth !== true) return;

        intervalRef.current = setInterval(() => {
            const objSesion = JSON.parse(sessionStorage.getItem('Usuario'));
            if (!objSesion) {
                clearInterval(intervalRef.current);
                return;
            }
            verificarSesion(objSesion.sesionId, objSesion.usuario).then(resultado => {
                if (!resultado) {
                    if (!sinConexionRef.current) {
                        sinConexionRef.current = true;
                        toast.warn('Se perdió la conexión con el servidor. Se reintentará.');
                    }
                    return;
                }
                sinConexionRef.current = false;
                if (!resultado.valida) {
                    mostrarAlertaSesionRef.current(resultado.mensaje);
                }
            });
        }, POLL_INTERVAL_MS);

        return () => clearInterval(intervalRef.current);
    }, [auth]);

    useEffect(() => {
        if (auth !== true) return;

        const originalFetch = window.fetch;
        window.fetch = async (...args) => {
            const response = await originalFetch(...args);
            const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
            if (url.startsWith(urlApi)) {
                response.clone().json().then(data => {
                    if (data?.status === 1 && data?.objError?.msgError) {
                        const msg = data.objError.msgError.toLowerCase();
                        if (msg.includes('sesi')) {
                            mostrarAlertaSesionRef.current(data.objError.msgError);
                        }
                    }
                }).catch(() => {});
            }
            return response;
        };

        return () => { window.fetch = originalFetch; };
    }, [auth]);


    if(auth === undefined) {
        return sinConexionInicial
            ? <div className="d-flex justify-content-center align-items-center vh-100">No hay conexión con el servidor. Reintentando…</div>
            : null;
    }

        return (
            <>
            <AuthContext.Provider value={[auth, setAuth]}>
                {props.children}
            </AuthContext.Provider>
            <Modal isOpen={sesionExpirada.visible} centered backdrop="static" keyboard={false}>
                <ModalHeader>Sesión expirada</ModalHeader>
                <ModalBody>{sesionExpirada.mensaje}</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={confirmarSesionExpirada}>Aceptar</Button>
                </ModalFooter>
            </Modal>
            </>
        );
    
    
}

export default AuthProvider;

